import type { ChangeEvent } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  FormHelperText,
  Grid,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { downloadFileFromS3 } from '../../api/aws/s3';
import {
  clientOrientation,
  issuesRef
} from '../../api/firebase/firestore/issue';
import { clientRegisterOrientationSchema } from '../../api/firebase/firestore/issue/validation';
import { usersRef } from '../../api/firebase/firestore/user';
import {
  article,
  attention,
  proposalSwitcher
} from '../../constants/CheckboxList';
import {
  black,
  blackText,
  blue,
  blueGrayText,
  cardBorder,
  gray,
  primaryButtonBackground,
  primarycolor,
  tertiaryButtonBackground
} from '../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  formWideMb,
  textFontSize,
  textMb,
  titleFontSize,
  titleMb
} from '../../constants/Location';
import { clientSteps } from '../../constants/RegisterStep';
import { screenType } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomCheckbox from './CustomCheckbox';
import CustomDatePicker from './CustomDatePicker';
import CustomFileDownload from './CustomFileDownload';
import CustomGuideButton from './CustomGuideButton';
import CustomLoading from './CustomLoading';
import CustomSnackbar from './CustomSnackbar';
import CustomStepper from './CustomStepper';
import CustomTextfield from './CustomTextfield';

import type { Dayjs } from 'dayjs';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

export interface ClientRegisterOrientationType {
  product?: string | null;
  productUrl?: string | null;
  launchDate?: Dayjs | null;
  price?: string | undefined;
  productExplain?: string | null;
  appealPoint?: string | null;
  salesChannel?: string | null;
  reputation?: string | null;
  article?: boolean[];
  articleOther?: string | null;
  proposal?: boolean[];
  proposalOther?: string | null;
  attention?: boolean[];
  attentionOther?: string | null;
}

const styles = {
  buttonFont,
  cardBorder,
  cardContentPadding,
  cardSize,
  datePicker: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: black
      }
    },
    '@media screen and (max-width:480px)': {
      width: '148px'
    },
    width: '180px'
  },
  description: {
    color: blueGrayText,
    fontSize: '14px',
    marginBottom: '24px'
  },
  divider: {
    backgroundColor: '#DBDBE0',
    color: black
  },
  downloadButton: {
    color: black,
    fontSize: '13px',
    height: '36px',
    padding: '4px 10px',
    width: '140px'
  },
  fileLabel: {
    color: black,
    fontSize: '16px',
    margin: '8px 0'
  },
  helper: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    }
  },
  label: {
    fontSize: '12px'
  },
  skipButton: {
    color: blue,
    height: '36px',
    width: '140px'
  },
  submitButton: {
    '@media screen and (max-width:960px)': {
      height: '42px'
    }
  },
  textFontSize,
  textMb,
  title: {
    color: blackText,
    fontSize: titleFontSize
  },
  titleMb,
  undoButton: {
    border: `1px solid ${gray}`,
    color: gray,
    height: '42px',
    marginRight: '16px'
  },
  uploadButton: {
    '&:hover': {
      backgroundColor: black,
      boxShadow: 'none',
      opacity: 0.7
    },
    backgroundColor: black,
    boxShadow: 'none',
    fontSize: '13px',
    height: '36px',
    padding: '4px 10px',
    width: '140px'
  }
};

interface Props {
  screen: number;
  copyIssueId?: string;
}

const RegisterOrientationCard: React.FC<Props> = (props): JSX.Element => {
  const { screen, copyIssueId } = props;
  const { t } = useTranslation();
  const steps = clientSteps();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useContext(AuthContext);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [issueData, setIssueData] = useState<DocumentData>();

  const currentPage = location.pathname;
  const registerOrientation = '/auth/orientation';
  const pageUrl = new URL(window.location.href);
  const params = pageUrl.searchParams;
  const issueId = params.get('id')?.split('?').join('') ?? '';
  const type = params.get('type')?.split('?').join('') ?? '';

  const {
    control,
    watch,
    setValue,
    register,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm<ClientRegisterOrientationType>({
    defaultValues: {
      launchDate: null,
      price: '',
      product: '',
      productUrl: '',
      salesChannel: ''
    },
    resolver: yupResolver(clientRegisterOrientationSchema)
  });

  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        (watch('article.99') && Boolean(!watch('articleOther'))) ||
        (watch('proposal.99') && Boolean(!watch('proposalOther'))) ||
        (watch('attention.99') && Boolean(!watch('attentionOther'))) ||
        Boolean(errors.product) ||
        Boolean(errors.productUrl) ||
        Boolean(errors.launchDate) ||
        Boolean(errors.price) ||
        Boolean(errors.productExplain) ||
        Boolean(errors.appealPoint) ||
        Boolean(errors.salesChannel) ||
        Boolean(errors.reputation) ||
        Boolean(errors.article) ||
        (watch('article.99') && Boolean(errors.articleOther)) ||
        Boolean(errors.proposal) ||
        (watch('proposal.99') && Boolean(errors.proposalOther)) ||
        Boolean(errors.attention) ||
        (watch('attention.99') && Boolean(errors.attentionOther)) ||
        Boolean(errorNumberOfFiles) ||
        Boolean(errorNumberOfSpecFiles) ||
        Boolean(errorNumberOfProposalFiles)
      );
    }
    return false;
  };

  const [finishStatus, setfinishStatus] = useState(false);
  const [closeButton, setCloseButton] = useState(false);

  const browserBackAlert = (e: { preventDefault: () => void }): void => {
    if (!finishStatus) {
      e.preventDefault();
      setCloseButton(true);
      setOpen(true);
      setErrorText(t('form.browserBackAlert'));
      window.history.pushState(null, '', window.location.pathname);
      setfinishStatus(false);
    }
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ): void => {
    if (reason === 'clickaway') return;
    setOpen(false);
    setCloseButton(false);
  };

  useEffect(() => {
    if (currentPage.includes('auth')) {
      history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', browserBackAlert);
      return () => {
        window.removeEventListener('popstate', browserBackAlert);
      };
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const prepareIssueData = async (): Promise<string> => {
    let issueDataId = '';
    const q = query(issuesRef, where('assigneeId', '==', currentUser?.uid));
    const querySnapshot = await getDocs(q);
    querySnapshot.docs.forEach(
      (document: QueryDocumentSnapshot<DocumentData>): void => {
        issueDataId = document.data()?.issueId as string;
      }
    );
    return issueDataId;
  };

  const undoToIssue = async (): Promise<void> => {
    if (currentUser?.uid) {
      setLoading(true);
      if (screen === screenType.client) {
        if (currentPage.includes('auth')) {
          const id = await prepareIssueData();
          await clientOrientation(
            currentUser?.uid,
            watch,
            issueId,
            files,
            deleteFiles,
            specFiles,
            specDeleteFiles,
            proposalFiles,
            proposalDeleteFiles
          );
          navigate(`/client/auth/issue?type=edit&id=${id}`);
        } else {
          await clientOrientation(
            currentUser?.uid,
            watch,
            issueId,
            files,
            deleteFiles,
            specFiles,
            specDeleteFiles,
            proposalFiles,
            proposalDeleteFiles
          );
          navigate(`/client/mypage/issue/edit?type=edit&id=${issueId}`);
        }
      } else if (screen === screenType.agent) {
        if (currentPage.includes('auth')) {
          const id = await prepareIssueData();
          await clientOrientation(
            currentUser?.uid,
            watch,
            issueId,
            files,
            deleteFiles,
            specFiles,
            specDeleteFiles,
            proposalFiles,
            proposalDeleteFiles
          );
          navigate(`/agent/auth/issue?type=edit&id=${id}`);
        } else {
          await clientOrientation(
            currentUser?.uid,
            watch,
            issueId,
            files,
            deleteFiles,
            specFiles,
            specDeleteFiles,
            proposalFiles,
            proposalDeleteFiles
          );
          navigate(`/agent/mypage/issue/edit?type=edit&id=${issueId}`);
        }
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      setOpen(true);
      setErrorText(t('form.loginError'));
      setTimeout(() => {
        navigate('/auth/login');
      }, 6000);
    }
  }, []);

  const [issueCompanyId, setIssueCompanyId] = useState<string>('');
  const [issueAgentId, setIssueAgentId] = useState<string>('');

  // 自社の与件ではないときは前のページに戻る
  useEffect(() => {
    void (async () => {
      const userRef = doc(usersRef, currentUser?.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      if (userData) {
        const userCompany = userData.companyId as string;

        // 広告主のとき
        if (screen === screenType.client && issueCompanyId) {
          if (userCompany !== issueCompanyId) {
            navigate('/auth/login');
          }
        }

        // 代理店のとき
        if (screen === screenType.agent && issueAgentId) {
          if (userCompany !== issueAgentId) {
            navigate('/auth/login');
          }
        }

        // 代理店経由で登録した与件は広告主は編集不可
        if (
          screen === screenType.client &&
          issueAgentId !== '' &&
          issueAgentId !== null &&
          issueAgentId !== undefined
        ) {
          if (userCompany !== issueCompanyId) {
            navigate('/auth/login');
          }
        }
      }
    })();
  }, [issueCompanyId, issueAgentId]);

  useEffect(() => {
    !watch('article.99') && setValue('articleOther', '');
  }, [watch('article.99')]);

  useEffect(() => {
    watch('articleOther') && setValue('article.99', true);
  }, [watch('articleOther')]);

  useEffect(() => {
    !watch('proposal.99') && setValue('proposalOther', '');
  }, [watch('proposal.99')]);

  useEffect(() => {
    watch('proposalOther') && setValue('proposal.99', true);
  }, [watch('proposalOther')]);

  useEffect(() => {
    !watch('attention.99') && setValue('attentionOther', '');
  }, [watch('attention.99')]);

  useEffect(() => {
    watch('attentionOther') && setValue('attention.99', true);
  }, [watch('attentionOther')]);

  useEffect(() => {
    void (async () => {
      if (issueId && !copyIssueId) {
        const issueDocRed = doc(issuesRef, issueId);
        const docSnap = await getDoc(issueDocRed);
        setIssueData(docSnap.data());
      } else if (copyIssueId) {
        const issueDocRed = doc(issuesRef, copyIssueId);
        const docSnap = await getDoc(issueDocRed);
        setIssueData(docSnap.data());
      }
    })();
  }, []);

  useEffect(() => {
    if (issueData) {
      const checkboxBooleanArray = (
        data: number[],
        length?: number
      ): boolean[] => {
        if (length) {
          const newArray = [];
          for (let i = 0; i <= length; i++) {
            newArray.push(false);
          }
          data.forEach((element: number) => {
            newArray[element] = true;
          });
          return newArray;
        }
        return [];
      };

      interface timestamp {
        nanoseconds: number;
        seconds: number;
        start: {
          nanoseconds: number;
          seconds: number;
        };
        end: {
          nanoseconds: number;
          seconds: number;
        };
      }

      setIssueCompanyId(issueData.companyId as string);
      setIssueAgentId(issueData.agentId as string);

      reset({
        appealPoint: issueData.appealPoint as string | null,
        article:
          (issueData.articleContent as number[]) &&
          checkboxBooleanArray(
            issueData.articleContent as number[],
            watch('article')?.length
          ),
        articleOther: (issueData.articleContentOther as string) || null,
        attention:
          (issueData.ngContent as number[]) &&
          checkboxBooleanArray(
            issueData.ngContent as number[],
            watch('attention')?.length
          ),
        attentionOther: (issueData.ngContentOther as string) || null,
        launchDate: issueData.launchDate
          ? dayjs.unix((issueData.launchDate as timestamp).seconds)
          : null,
        price: issueData.price as string | undefined,
        product: issueData.productName as string | null,
        productExplain: issueData.productExplain as string | null,
        productUrl: issueData.productUrl as string | null,
        proposal:
          (issueData.proposalContent as number[]) &&
          checkboxBooleanArray(
            issueData.proposalContent as number[],
            watch('proposal')?.length
          ),
        proposalOther: (issueData.proposalContentOther as string) || null,
        reputation: issueData.reputation as string | null,
        salesChannel: issueData.salesChannel as string | null
      });
      type === 'edit' &&
        (issueData.imageId as string[]) &&
        (issueData.imageId as string[]).forEach((item: string) => {
          setFileNames((fileNames) => [...fileNames, item]);
          setPreview((preview) => [...preview, '']);
          setFiles((files) => [...files, item]);
        });
      type === 'edit' &&
        (issueData.specFile as string[]) &&
        (issueData.specFile as string[]).forEach((item: string) => {
          setSpecFileNames((fileNames) => [...fileNames, item]);
          setSpecPreview((preview) => [...preview, '']);
          setSpecFiles((files) => [...files, item]);
        });
      type === 'edit' &&
        (issueData.proposalFile as string[]) &&
        (issueData.proposalFile as string[]).forEach((item: string) => {
          setProposalFileNames((fileNames) => [...fileNames, item]);
          setProposalPreview((preview) => [...preview, '']);
          setProposalFiles((files) => [...files, item]);
        });
    }
  }, [issueData]);

  const skipRegister = (): void => {
    setLoading(true);
    setTimeout(() => {
      if (currentPage.includes('client')) {
        navigate(`/client/auth/complete`);
      } else if (currentPage.includes('agent')) {
        navigate('/agent/auth/complete');
      } else {
        navigate('/client/auth/complete');
      }
    }, 800);
  };

  const onSubmit = async (): Promise<void> => {
    try {
      if (currentUser?.uid) {
        setLoading(true);
        await clientOrientation(
          currentUser?.uid,
          watch,
          issueId,
          files,
          deleteFiles,
          specFiles,
          specDeleteFiles,
          proposalFiles,
          proposalDeleteFiles
        );
        if (currentPage.includes('mypage')) {
          if (screen === screenType.client) {
            navigate(
              `/client/mypage/issue/complete?type=${type}&id=${issueId}`
            );
          } else if (screen === screenType.agent) {
            navigate(`/agent/mypage/issue/complete?type=${type}&id=${issueId}`);
          }
        } else if (screen === screenType.client) {
          navigate('/client/auth/complete');
        } else if (screen === screenType.agent) {
          navigate('/agent/auth/complete');
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  // 画像アップロード
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [preview, setPreview] = useState<string[]>([]);
  const [files, setFiles] = useState<Array<FileList | string>>([]);
  const [deleteFiles, setDeleteFiles] = useState<string[]>([]);
  const [errorNumberOfFiles, setErrorNumberOfFiles] = useState<boolean>(false);

  useEffect(() => {
    setErrorNumberOfFiles(fileNames.length > 5);
  }, [fileNames]);

  const changeUploadFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setFileNames([...fileNames, file[0].name]);
      setPreview([...preview, window.URL.createObjectURL(blob)]);
      setFiles([...files, file]);
    }
  };

  const deleteUploadFile = (i: number, name: string): void => {
    if (name.match(/issues\/.*\/image\//g)) {
      setDeleteFiles([...deleteFiles, name]);
    }
    setFileNames(fileNames.filter((file, index) => index !== i));
    setPreview(preview.filter((file, index) => index !== i));
    setFiles(files.filter((file, index) => index !== i));
  };

  const downloadFile = (i: number, name: string): void => {
    if (!name.match(/issues\/.*\/image\//g)) {
      const url = preview[i];
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.download = `${fileNames[i]}`;
      a.href = url;
      a.click();
    } else {
      void (async () => {
        await downloadFileFromS3(name);
      })();
    }
  };

  // スペックシートアップロード
  const [specFileNames, setSpecFileNames] = useState<string[]>([]);
  const [specPreview, setSpecPreview] = useState<string[]>([]);
  const [specFiles, setSpecFiles] = useState<Array<FileList | string>>([]);
  const [specDeleteFiles, setSpecDeleteFiles] = useState<string[]>([]);
  const [errorNumberOfSpecFiles, setErrorNumberOfSpecFiles] =
    useState<boolean>(false);

  useEffect(() => {
    setErrorNumberOfSpecFiles(specFileNames.length > 1);
  }, [specFileNames]);

  const changeUploadSpecFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setSpecFileNames([...specFileNames, file[0].name]);
      setSpecPreview([...specPreview, window.URL.createObjectURL(blob)]);
      setSpecFiles([...specFiles, file]);
    }
  };

  const deleteUploadSpecFile = (i: number, name: string): void => {
    if (name.match(/issues\/.*\/image\//g)) {
      setSpecDeleteFiles([...specDeleteFiles, name]);
    }
    setSpecFileNames(specFileNames.filter((file, index) => index !== i));
    setSpecPreview(specPreview.filter((file, index) => index !== i));
    setSpecFiles(specFiles.filter((file, index) => index !== i));
  };

  const downloadSpecFile = (i: number, name: string): void => {
    if (!name.match(/issues\/.*\/image\//g)) {
      const url = specPreview[i];
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.download = `${specFileNames[i]}`;
      a.href = url;
      a.click();
    } else {
      void (async () => {
        await downloadFileFromS3(name);
      })();
    }
  };

  // 提案フォーマットアップロード
  const [proposalFileNames, setProposalFileNames] = useState<string[]>([]);
  const [proposalPreview, setProposalPreview] = useState<string[]>([]);
  const [proposalFiles, setProposalFiles] = useState<Array<FileList | string>>(
    []
  );
  const [proposalDeleteFiles, setProposalDeleteFiles] = useState<string[]>([]);
  const [errorNumberOfProposalFiles, setErrorNumberOfProposalFiles] =
    useState<boolean>(false);

  useEffect(() => {
    setErrorNumberOfProposalFiles(proposalFileNames.length > 1);
  }, [proposalFileNames]);

  const changeUploadProposalFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setProposalFileNames([...proposalFileNames, file[0].name]);
      setProposalPreview([
        ...proposalPreview,
        window.URL.createObjectURL(blob)
      ]);
      setProposalFiles([...proposalFiles, file]);
    }
  };

  const deleteUploadProposalFile = (i: number, name: string): void => {
    if (name.match(/issues\/.*\/image\//g)) {
      setProposalDeleteFiles([...proposalDeleteFiles, name]);
    }
    setProposalFileNames(
      proposalFileNames.filter((file, index) => index !== i)
    );
    setProposalPreview(proposalPreview.filter((file, index) => index !== i));
    setProposalFiles(proposalFiles.filter((file, index) => index !== i));
  };

  const downloadProposalFile = (i: number, name: string): void => {
    if (!name.match(/issues\/.*\/image\//g)) {
      const url = proposalPreview[i];
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.download = `${proposalFileNames[i]}`;
      a.href = url;
      a.click();
    } else {
      void (async () => {
        await downloadFileFromS3(name);
      })();
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{ marginBottom: '24px', marginTop: '24px' }}
      >
        <Grid>
          <Card sx={[styles.cardSize, styles.cardBorder]}>
            <CardContent sx={styles.cardContentPadding}>
              {currentPage.includes(registerOrientation) && (
                <CustomStepper steps={steps} activeStep={3} />
              )}
              <Typography
                component="div"
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '16px'
                }}
              >
                <Typography sx={styles.title} component="div">
                  {t('registerBasic.step4title')}
                </Typography>
                {currentPage.includes(registerOrientation) && (
                  <Button
                    variant="text"
                    onClick={() => {
                      skipRegister();
                    }}
                    sx={styles.skipButton}
                  >
                    <Typography sx={{ fontSize: '14px' }}>
                      {t('form.skip')}
                    </Typography>
                    <ChevronRightIcon />
                  </Button>
                )}
              </Typography>
              <Typography sx={[styles.description]} component="div">
                {t('orientation.description')}
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('orientation.product')}
                    isRequired={false}
                    error={Boolean(errors.product)}
                    helperText={errors.product?.message}
                    placeholder=" "
                    guideText={t('guideText.orientation.product')}
                  />
                )}
                control={control}
                name="product"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('orientation.productUrl')}
                    isRequired={false}
                    type="url"
                    error={Boolean(errors.productUrl)}
                    helperText={errors.productUrl?.message}
                    placeholder=" "
                  />
                )}
                control={control}
                name="productUrl"
              />
              <Controller
                render={({ field }) => (
                  <CustomDatePicker
                    {...field}
                    labelText={t('orientation.launchDate')}
                    isRequired={false}
                    error={Boolean(errors.launchDate)}
                    helperText={errors.launchDate?.message}
                    placeholder="2022/07/24"
                    setValue={(newValue) => {
                      setValue('launchDate', newValue);
                    }}
                    onChangeForValide={field.onChange}
                  />
                )}
                control={control}
                name="launchDate"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('orientation.price')}
                    isRequired={false}
                    error={Boolean(errors.price)}
                    helperText={errors.price?.message}
                    placeholder=" "
                    guideText={t('guideText.orientation.price')}
                  />
                )}
                control={control}
                name="price"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('orientation.productExplain')}
                    isRequired={false}
                    multiline
                    rows={5}
                    fieldType="textareaRow5"
                    guideText={t('guideText.orientation.productExplain')}
                    error={Boolean(errors.productExplain)}
                    helperText={errors.productExplain?.message}
                    value={
                      watch('productExplain')
                        ? watch('productExplain')
                        : undefined
                    }
                  />
                )}
                control={control}
                name="productExplain"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('orientation.appealPoint')}
                    isRequired={false}
                    multiline
                    rows={3}
                    fieldType="textareaRow3"
                    guideText={t('guideText.orientation.appealPoint')}
                    error={Boolean(errors.appealPoint)}
                    helperText={errors.appealPoint?.message}
                    value={
                      watch('appealPoint') ? watch('appealPoint') : undefined
                    }
                  />
                )}
                control={control}
                name="appealPoint"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('orientation.salesChannel')}
                    isRequired={false}
                    error={Boolean(errors.salesChannel)}
                    helperText={errors.salesChannel?.message}
                    guideText={t('guideText.orientation.salesChannel')}
                  />
                )}
                control={control}
                name="salesChannel"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    labelText={t('orientation.reputation')}
                    isRequired={false}
                    multiline
                    rows={2}
                    fieldType="textareaRow2"
                    error={Boolean(errors.reputation)}
                    helperText={errors.reputation?.message}
                    value={
                      watch('reputation') ? watch('reputation') : undefined
                    }
                    guideText={t('guideText.orientation.reputation')}
                  />
                )}
                control={control}
                name="reputation"
              />
              <CustomCheckbox
                labelText={t('orientation.proposal')}
                isRequired={false}
                list={
                  issueData
                    ? proposalSwitcher(
                        issueData.mediaType as
                          | number[]
                          | boolean
                          | null
                          | undefined,
                        t
                      )
                    : []
                }
                formMb="extraNarrow"
                control={control}
                name="proposal"
                registerOrientation={register}
                error={!watch('proposal.99') && Boolean(errors.proposal)}
                helperText={errors.proposal?.message}
                watchOrientation={watch}
              />
              <Typography component="div" sx={{ marginLeft: '25px' }}>
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired={false}
                      multiline
                      rows={3}
                      fieldType="textareaRow3"
                      width="other"
                      error={
                        watch('proposal.99') && Boolean(errors.proposalOther)
                      }
                      helperText={errors.proposalOther?.message}
                      value={
                        watch('proposalOther')
                          ? watch('proposalOther')
                          : undefined
                      }
                    />
                  )}
                  control={control}
                  name="proposalOther"
                />
              </Typography>
              <CustomCheckbox
                labelText={t('orientation.article')}
                isRequired={false}
                list={article()}
                formMb="extraNarrow"
                control={control}
                name="article"
                registerOrientation={register}
                error={!watch('article.99') && Boolean(errors.article)}
                helperText={errors.article?.message}
                watchOrientation={watch}
              />
              <Typography component="div" sx={{ marginLeft: '25px' }}>
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired={false}
                      multiline
                      rows={3}
                      fieldType="textareaRow3"
                      width="other"
                      error={
                        watch('article.99') && Boolean(errors.articleOther)
                      }
                      helperText={errors.articleOther?.message}
                      value={
                        watch('articleOther')
                          ? watch('articleOther')
                          : undefined
                      }
                    />
                  )}
                  control={control}
                  name="articleOther"
                />
              </Typography>
              <CustomCheckbox
                labelText={t('orientation.attention')}
                isRequired={false}
                list={attention()}
                formMb="extraNarrow"
                control={control}
                name="attention"
                registerOrientation={register}
                error={!watch('attention.99') && Boolean(errors.attention)}
                helperText={errors.attention?.message}
                watchOrientation={watch}
              />
              <Typography component="div" sx={{ marginLeft: '25px' }}>
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired={false}
                      multiline
                      rows={3}
                      fieldType="textareaRow3"
                      width="other"
                      error={
                        watch('attention.99') && Boolean(errors.attentionOther)
                      }
                      helperText={errors.attentionOther?.message}
                      value={
                        watch('attentionOther')
                          ? watch('attentionOther')
                          : undefined
                      }
                    />
                  )}
                  control={control}
                  name="attentionOther"
                />
              </Typography>

              <Typography component="div" sx={{ marginBottom: formWideMb }}>
                <Typography color={gray} sx={styles.label} component="span">
                  {t('orientation.image')}
                </Typography>
                <CustomGuideButton
                  title={t('orientation.image')}
                  guideText={t('guideText.orientation.image')}
                />
                {type === 'copy' && fileNames.length === 0 && (
                  <Typography
                    component="p"
                    sx={[styles.label, { color: primarycolor, m: '4px 0' }]}
                  >
                    {t('orientation.copyDesc')}
                  </Typography>
                )}
                <Typography
                  component="div"
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <Typography
                    sx={[
                      styles.label,
                      {
                        color: black,
                        margin: '8px 0',
                        whiteSpace: 'pre-wrap'
                      }
                    ]}
                  >
                    {t('orientation.imageDescription')}
                  </Typography>
                  <Button
                    component="label"
                    variant="contained"
                    sx={styles.uploadButton}
                  >
                    <input
                      accept=".jpg, .jpeg, .png, .webp, .gif, .svg"
                      type="file"
                      hidden
                      onChange={(value) => {
                        changeUploadFile(value);
                      }}
                    />
                    <AddIcon sx={{ color: '#FFF' }} />
                    {t('orientation.imageUploadButton')}
                  </Button>
                </Typography>
                <FormHelperText sx={styles.helper} error={errorNumberOfFiles}>
                  {errorNumberOfFiles && t('validation.error.numberOfImage')}
                </FormHelperText>
                <Typography component="div" sx={{ mb: '32px', mt: '8px' }}>
                  <Divider light sx={[styles.divider]} />
                  {fileNames.map((fileName, index) => (
                    <CustomFileDownload
                      download={downloadFile}
                      deleteFunc={deleteUploadFile}
                      key={index}
                      fileName={fileName}
                      index={index}
                    />
                  ))}
                </Typography>
              </Typography>

              <Typography component="div" sx={{ marginBottom: formWideMb }}>
                <Grid container flexDirection={'row'} alignItems={'center'}>
                  <Typography color={gray} sx={styles.label} component="p">
                    {t('orientation.specSheetDesc')}
                  </Typography>
                  <CustomGuideButton
                    title={t('orientation.proposalSheet')}
                    guideText={t('guideText.orientation.proposalSheet')}
                  />
                </Grid>
                <Typography sx={styles.fileLabel} component="p">
                  {t('orientation.specSheet')}
                </Typography>
                {type === 'copy' && specFileNames.length === 0 && (
                  <Typography
                    component="p"
                    sx={[styles.label, { color: primarycolor, m: '4px 0' }]}
                  >
                    {t('orientation.copyfileDesc')}
                  </Typography>
                )}
                <Typography
                  component="div"
                  sx={{
                    columnGap: '12px',
                    display: 'flex',
                    flexDirection: 'row'
                  }}
                >
                  <Button
                    component="label"
                    variant="contained"
                    sx={styles.uploadButton}
                  >
                    <input
                      accept=".pdf, .ppt, .pptx, .potx, .pot, .odp, .ppsx, .pps, .pptm, .potm, .ppsm, .doc, .docx, .dotx, .dot, .docm, .dotm, .odt, .xls, .xlsx, .xltx, .xlt, .xlsm, .xlsb, .xltm, .prn, .dif, .slk, .xlam, .xla, .ods, .txt, .rtf, .csv, .tsv .jpg, .jpeg, .png, .gif, .svg, .webp"
                      type="file"
                      hidden
                      onChange={(value) => {
                        changeUploadSpecFile(value);
                      }}
                    />
                    <AddIcon sx={{ color: '#FFF' }} />
                    {t('orientation.imageUploadButton')}
                  </Button>

                  <Button
                    component="label"
                    variant="outlined"
                    sx={[tertiaryButtonBackground, styles.downloadButton]}
                    onClick={async () =>
                      await downloadFileFromS3(
                        'format/【MediAge】メニューリストシート.xlsx'
                      )
                    }
                  >
                    <FileDownloadOutlinedIcon sx={{ color: black }} />
                    {t('orientation.templete')}
                  </Button>
                </Typography>
                <FormHelperText
                  sx={styles.helper}
                  error={errorNumberOfSpecFiles}
                >
                  {errorNumberOfSpecFiles && t('validation.error.numberOfFile')}
                </FormHelperText>
                <Typography component="div" sx={{ mb: '32px', mt: '8px' }}>
                  <Divider light sx={[styles.divider]} />
                  {specFileNames.map((fileName, index) => (
                    <CustomFileDownload
                      download={downloadSpecFile}
                      deleteFunc={deleteUploadSpecFile}
                      key={index}
                      fileName={fileName}
                      index={index}
                    />
                  ))}
                </Typography>
              </Typography>

              <Typography component="div" sx={{ marginBottom: formWideMb }}>
                <Typography sx={styles.fileLabel} component="p">
                  {t('orientation.proposalSheet')}
                </Typography>
                {type === 'copy' && proposalFileNames.length === 0 && (
                  <Typography
                    component="p"
                    sx={[styles.label, { color: primarycolor, m: '4px 0' }]}
                  >
                    {t('orientation.copyfileDesc')}
                  </Typography>
                )}
                <Typography
                  component="div"
                  sx={{
                    columnGap: '12px',
                    display: 'flex',
                    flexDirection: 'row'
                  }}
                >
                  <Button
                    component="label"
                    variant="contained"
                    sx={styles.uploadButton}
                  >
                    <input
                      accept=".pdf, .ppt, .pptx, .potx, .pot, .odp, .ppsx, .pps, .pptm, .potm, .ppsm, .doc, .docx, .dotx, .dot, .docm, .dotm, .odt, .xls, .xlsx, .xltx, .xlt, .xlsm, .xlsb, .xltm, .prn, .dif, .slk, .xlam, .xla, .ods, .txt, .rtf, .csv, .tsv .jpg, .jpeg, .png, .gif, .svg, .webp"
                      type="file"
                      hidden
                      onChange={(value) => {
                        changeUploadProposalFile(value);
                      }}
                    />
                    <AddIcon sx={{ color: '#FFF' }} />
                    {t('orientation.imageUploadButton')}
                  </Button>

                  <Button
                    component="label"
                    variant="outlined"
                    sx={[tertiaryButtonBackground, styles.downloadButton]}
                    onClick={async () =>
                      await downloadFileFromS3(
                        'format/【MediAge】規定フォーマットPPT.pptx'
                      )
                    }
                  >
                    <FileDownloadOutlinedIcon sx={{ color: black }} />
                    {t('orientation.templete')}
                  </Button>
                </Typography>
                <FormHelperText
                  sx={styles.helper}
                  error={errorNumberOfProposalFiles}
                >
                  {errorNumberOfProposalFiles &&
                    t('validation.error.numberOfFile')}
                </FormHelperText>
                <Typography component="div" sx={{ mb: '32px', mt: '8px' }}>
                  <Divider light sx={[styles.divider]} />
                  {proposalFileNames.map((fileName, index) => (
                    <CustomFileDownload
                      download={downloadProposalFile}
                      deleteFunc={deleteUploadProposalFile}
                      key={index}
                      fileName={fileName}
                      index={index}
                    />
                  ))}
                </Typography>
              </Typography>

              <CardActions sx={[styles.textMb, { mt: '8px', padding: 0 }]}>
                <Grid container>
                  <Grid item xs={3} sx={{ pr: '8px' }}>
                    <Button
                      disabled={buttonDisabledFlag()}
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        void (async () => {
                          setFirstSubmit(true);
                          await handleSubmit(async () => {
                            if (
                              !errorNumberOfFiles &&
                              !errorNumberOfSpecFiles &&
                              !errorNumberOfProposalFiles
                            ) {
                              await undoToIssue();
                            }
                          })();
                        })();
                      }}
                      sx={styles.undoButton}
                    >
                      <Typography sx={{ fontSize: '14px' }}>
                        {t('passwordReset.back')}
                      </Typography>
                    </Button>
                  </Grid>
                  <Grid item xs={9}>
                    <Button
                      disabled={buttonDisabledFlag()}
                      variant="contained"
                      fullWidth
                      sx={[primaryButtonBackground, styles.submitButton]}
                      onClick={() => {
                        void (async () => {
                          setFirstSubmit(true);
                          await handleSubmit(async () => {
                            if (
                              !errorNumberOfFiles &&
                              !errorNumberOfSpecFiles &&
                              !errorNumberOfProposalFiles
                            ) {
                              await onSubmit();
                            }
                          })();
                        })();
                      }}
                    >
                      <Typography
                        component="div"
                        color="white"
                        sx={{ fontSize: 15 }}
                      >
                        {t('orientation.registerButton')}
                      </Typography>
                    </Button>
                  </Grid>
                </Grid>
              </CardActions>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={closeButton ? handleClose : () => setOpen(false)}
        text={errorText}
        {...(closeButton && { closeButton: handleClose })}
      />
    </>
  );
};

export default RegisterOrientationCard;
